import { debounce } from 'lodash';
import { Pivot, PivotItem } from '@fluentui/react';
import { SearchBox } from '@fluentui/react';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ILocationApplication } from '../../../shared/api/applications/applications';
import * as Applications from '../../../shared/api/applications/applications';
import * as UserPreferences from '../../../shared/api/applications/userPreferences';
import * as Configuration from '../../../shared/api/configuration/configuration';
import { filterApplications } from '../../../shared/api/applications/applications';
import * as Profiles from '../../../shared/api/profiles/profiles';
import AppsByCategory from './AppsByCategory';
import AppsSorted from './AppsSorted';
import { WorkTracker } from '../../../shared/components/LoadingPanel/work-tracker';
import LoadingPanel from '../../../shared/components/LoadingPanel/LoadingPanel';
import { useLocation, useNavigate } from 'react-router-dom';
import './Apps.scss';
import { UserFavoriteAppDto } from '../../../shared/api/models';
import { EventBus, EventNames } from '../../../shared/eventbus/eventbus';
import { toast } from 'react-toastify';
import { AppTabKeys } from '../../routes/MainApp/MainApp';

interface IAppProps {
    activeTab: AppTabKeys;
    onSetActiveTab: (activeTab: AppTabKeys) => void;
}

export interface IApplicationOperations {
    isFavorite: (app: ILocationApplication) => boolean;
    canAddToFavorites: () => boolean;
    onToggleBelongingnessToFavorites: (app: ILocationApplication) => void;
}

const Apps: React.FC<IAppProps> = ({ activeTab, onSetActiveTab }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchPhrase, setSearchPhrase] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [applications, setApplications] = useState<ILocationApplication[] | null>(null);
    const [favoriteApps, setFavoriteApps] = useState<UserFavoriteAppDto[]>([]);
    const [favoriteAppsByKey, setFavoriteAppsByKey] = useState<{ [uniqueKey: string]: UserFavoriteAppDto }>({});
    const [favoriteAppsLimit, setFavoriteAppsLimit] = useState(0);
    const [, setCategoriesById] = useState<{ [categoryId: string]: Applications.CategoryViewModel }>({});
    const [userProfiles, setUserProfiles] = useState<string[]>([]);

    const workTracker = useMemo(() => new WorkTracker(), []);
    const abortControllerRef = useRef<AbortController | null>(null);
    const debouncedSearchPhraseRef = useRef<ReturnType<typeof debounce> | null>(null);

    const makeFavoriteAppKey = (favApp: UserFavoriteAppDto) => `${favApp.appId}|${favApp.locationCode}`;

    const onFavouriteAppsModified = (newFavAppsList: UserFavoriteAppDto[]) => {
        setFavoriteApps(newFavAppsList);
        setFavoriteAppsByKey(
            newFavAppsList.reduce((map: { [idAndLocationKey: string]: UserFavoriteAppDto }, favApp) => {
                map[makeFavoriteAppKey(favApp)] = favApp;
                return map;
            }, {}),
        );
    };

    const debouncedSearchPhraseChanged = useMemo(() => {
        if (debouncedSearchPhraseRef.current) {
            debouncedSearchPhraseRef.current.cancel();
        }
        const debounced = debounce((phrase: string) => {
            setCategoriesById((prev) => {
                Object.keys(prev).forEach((id) => {
                    const category = prev[id];
                    if (category.isCollapsed) {
                        category.toggle();
                    }
                });
                return { ...prev };
            });
            setSearchPhrase(phrase);
        }, 100);
        debouncedSearchPhraseRef.current = debounced;
        return debounced;
    }, []);

    useEffect(() => {
        return () => {
            if (debouncedSearchPhraseRef.current) {
                debouncedSearchPhraseRef.current.cancel();
            }
        };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        if (location.pathname === `/${AppTabKeys.AZ}`) {
            onSetActiveTab(AppTabKeys.AZ);
        }

        const loadApplications = async () => {
            const response = await Applications.getManyFlat(controller.signal);
            if (response.success && response.result) {
                setApplications(response.result.applications);
                setCategoriesById(response.result.categoriesById);
            }
        };

        const loadFavouriteApps = async () => {
            const response = await UserPreferences.get(controller.signal);
            if (response.success && response.result) {
                onFavouriteAppsModified(response.result.favoriteApps);
            }
        };

        const loadFavouriteAppsLimit = async () => {
            const response = await Configuration.get(controller.signal);
            if (response.success && response.result) {
                setFavoriteAppsLimit(response.result.sideDrawAppCountLimit);
            }
        };

        const loadMyProfiles = async () => {
            const response = await Profiles.getMine(controller.signal);
            if (response.success && response.result) {
                setUserProfiles(response.result.profiles?.map((profile) => profile.profileCode) ?? []);
            }
        };

        workTracker.track(Promise.all([loadApplications(), loadFavouriteApps(), loadFavouriteAppsLimit(), loadMyProfiles()])).then(() => {
            EventBus.emit(EventNames.MainApplicationLoaded);
        });

        EventBus.subscribe(EventNames.FavouriteAppListModified, onFavouriteAppsModified);

        return () => {
            EventBus.unsubscribe(EventNames.FavouriteAppListModified, onFavouriteAppsModified);
            controller.abort();
        };
    }, []);

    const isFavorite = (app: ILocationApplication) => {
        const appKey = makeFavoriteAppKey(app);
        return !!favoriteAppsByKey[appKey];
    };

    const canAddToFavorites = () => favoriteApps.length < favoriteAppsLimit;

    const updateFavoriteApps = async (newFavAppsList: UserFavoriteAppDto[]) => {
        const signal = abortControllerRef.current ? abortControllerRef.current.signal : undefined;
        const result = await workTracker.queueTrackingAndChain(UserPreferences.update({ favoriteApps: newFavAppsList }, signal));
        if (result.success) {
            EventBus.emit(EventNames.FavouriteAppListModified, newFavAppsList);
        }
    };

    const sendToastNotification = (message: string) =>
        toast(message, {
            className: 'ToggleAppToast',
            progressClassName: 'ToggleAppToastProgressBar',
        });

    const onToggleBelongingnessToFavorites = async (app: ILocationApplication) => {
        if (isFavorite(app)) {
            const newFavAppsList = favoriteApps.filter((favApp) => makeFavoriteAppKey(favApp) !== makeFavoriteAppKey(app));
            await updateFavoriteApps(newFavAppsList);
            sendToastNotification(`${app.appName} has been removed from your app launcher`);
        } else {
            const newFavApp: UserFavoriteAppDto = {
                appId: app.appId,
                locationCode: app.locationCode,
            };
            const newFavAppsList = [...favoriteApps, newFavApp];
            await updateFavoriteApps(newFavAppsList);
            sendToastNotification(`${app.appName} has been added to your app launcher`);
        }
    };

    const toggleCategory = (categoryId: string) => {
        setCategoriesById((prev) => {
            const foundCategory = prev[categoryId];
            if (foundCategory) {
                foundCategory.toggle();
            }
            return { ...prev };
        });
    };

    const clearSearch = () => {
        debouncedSearchPhraseRef.current?.cancel();
        setSearchInput('');
        setSearchPhrase('');
    };

    const switchToView = (tabKey: string) => {
        if (tabKey === AppTabKeys.AZ) {
            onSetActiveTab(AppTabKeys.AZ);
            navigate(`/${AppTabKeys.AZ}`, { replace: true });
        } else {
            onSetActiveTab(AppTabKeys.Category);
            navigate(`/${AppTabKeys.Category}`, { replace: true });
        }
    };

    const viewChanged = (item?: PivotItem) => {
        if (item) {
            switchToView(item.props.itemKey!);
        }
    };

    const applicationOperations: IApplicationOperations = {
        isFavorite,
        canAddToFavorites,
        onToggleBelongingnessToFavorites,
    };
    const filteredApplications = filterApplications(applications, searchPhrase);
    const hasSearch = Boolean(searchPhrase?.trim());

    return (
        <div className="AppList">
            <div className="ms-Grid ModeSelector" dir="ltr">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ModeSelectorControls">
                        <div id="AppSearchBox">
                            <SearchBox
                                ariaLabel="Search applications by name, description, tag or category"
                                placeholder="Search applications"
                                value={searchInput}
                                onChange={(_event, newValue) => {
                                    const value = newValue || '';
                                    setSearchInput(value);
                                    debouncedSearchPhraseChanged(value);
                                }}
                                onClear={clearSearch}
                            />
                        </div>
                        <Pivot onLinkClick={viewChanged} selectedKey={activeTab}>
                            <PivotItem
                                headerButtonProps={{ 'data-tab-button-id': 'ByCategoryButton' }}
                                headerText="By Category"
                                itemKey={AppTabKeys.Category}
                            />
                            <PivotItem headerText="A to Z" itemKey={AppTabKeys.AZ} />
                        </Pivot>
                    </div>
                </div>
            </div>

            <LoadingPanel workTracker={workTracker}>
                {applications && hasSearch && filteredApplications.length === 0 && (
                    <div className="NoSearchResults">
                        <p>No applications found for "{searchPhrase?.trim()}".</p>
                        <button type="button" onClick={clearSearch}>
                            Clear search
                        </button>
                    </div>
                )}
                {activeTab === AppTabKeys.AZ && filteredApplications.length > 0 && (
                    <AppsSorted
                        userProfiles={userProfiles}
                        applications={filteredApplications}
                        applicationOperations={applicationOperations}
                        form={hasSearch ? 'Search' : 'AZ'}
                    />
                )}
                {activeTab === AppTabKeys.Category && filteredApplications.length > 0 && (
                    <AppsByCategory
                        userProfiles={userProfiles}
                        applications={filteredApplications}
                        onToggleCategory={toggleCategory}
                        applicationOperations={applicationOperations}
                    />
                )}

                <footer>
                    <h1>Need help?</h1>
                    <div>For all other Online Services help go to ServiceNow</div>
                    <div>or contact the service desk on (07) 3033 7777</div>
                </footer>
            </LoadingPanel>
        </div>
    );
};

export default Apps;
